import React, { useState } from "react";
import "../styles/TestResults.scss";
import AiTutor from "./AiTutor";
import AnimatedCircularProgress from "./AnimatedCircularProgress";
import { sendAiTutorData } from "../helper/apicalls/test";
import Confetti from "react-confetti";

import AiGenerationPopup from "./AiGenerationPopup";

const TestResults = ({
  score,
  totalQuestions,
  onRetest,
  onShowAnswer,
  onExit,
  page_type,
  pass_percentage,
  userData,
  questions,
  pageId,
  pageKey,
  pageTitle,
}) => {
  const [showAiTutor, setShowAiTutor] = useState(false);
  const [showAiPopup, setShowAiPopup] = useState(false);
  const [aiLessons, setAiLessons] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const percentage = Math.round((score / totalQuestions) * 100);
  const showConfetti =
    typeof pass_percentage === "number" && percentage >= pass_percentage;

  const showTeachButton =
    page_type === "test" &&
    typeof pass_percentage === "number" &&
    percentage < pass_percentage;

  React.useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const greyTrackColor =
    getComputedStyle(document.documentElement)
      .getPropertyValue("--grey-track-color")
      .trim() || "#d1d1d1";

  const buildUserData = () => {
    if (!userData || !questions) return [];

    const buildOptions = (questionObj) => {
      if (!questionObj) return [];

      if (Array.isArray(questionObj.options)) {
        const letters = ["A", "B", "C", "D", "E", "F"];
        return questionObj.options.map((opt, idx) => {
          const key = letters[idx] || String(idx + 1);
          if (typeof opt === "string") {
            return { [key]: opt };
          }
          if (opt && typeof opt === "object") {
            const letterKey = Object.keys(opt).find((k) =>
              ["A", "B", "C", "D", "E", "F"].includes(k),
            );
            let text;
            if (letterKey) {
              text = opt[letterKey];
            } else {
              text = opt.text || opt.label || opt.value || opt.option || "";
            }
            return { [key]: text };
          }
          return { [key]: String(opt) };
        });
      }

      const letters = ["A", "B", "C", "D", "E", "F"];
      const collected = letters
        .filter(
          (key) =>
            Object.prototype.hasOwnProperty.call(questionObj, key) &&
            questionObj[key] !== undefined &&
            questionObj[key] !== null &&
            String(questionObj[key]).trim() !== "",
        )
        .map((key) => ({ [key]: questionObj[key] }));

      return collected;
    };

    return questions?.reduce((acc, q) => {
      let content = q.content;
      try {
        if (typeof content === "string") {
          content = JSON.parse(content);
        }
        if (typeof content === "string") {
          content = JSON.parse(content);
        }
      } catch (e) {
        return acc;
      }

      if (content && Array.isArray(content.questions)) {
        const explanationItemIndex = content.questions.findIndex(
          (item) => item.explanation && !item.question && !item.key,
        );
        if (explanationItemIndex !== -1) {
          content.explanation =
            content.questions[explanationItemIndex].explanation;
          content.questions = content.questions.filter(
            (_, i) => i !== explanationItemIndex,
          );
        }
      }

      if (q.sequence) {
        if (Array.isArray(content.questions)) {
          content.questions = content.questions.map((cq) => ({
            ...cq,
            question_id: cq.question_id || q.sequence,
          }));
        } else if (content && typeof content === "object") {
          content = {
            ...content,
            question_id: content.question_id || q.sequence,
          };
        }
      }

      const type = (q.type || "").toLowerCase().trim();

      const questionObj =
        type === "match" ? content : content?.questions?.[0] || content;
      if (!questionObj) return acc;

      const questionId =
        questionObj.question_id || q.sequence || questionObj.question;
      const rawUserAnswer = userData[questionId];
      if (!rawUserAnswer) return acc;

      if (type === "match") {
        if (!Array.isArray(rawUserAnswer)) return acc;

        const columns = content.questions || [];
        const leftCol = columns[0];
        const rightCol = columns[1];

        if (!leftCol || !rightCol) return acc;

        const terms = rawUserAnswer[0]?.values || [];
        const userDescriptions = rawUserAnswer[1]?.values || [];
        const correctTerms = leftCol.values || [];
        const correctDescriptions = rightCol.values || [];

        if (
          !terms.length ||
          !userDescriptions.length ||
          !correctTerms.length ||
          !correctDescriptions.length
        ) {
          return acc;
        }

        let correctCount = 0;
        for (let i = 0; i < terms.length; i++) {
          const term = terms[i];
          const userDesc = userDescriptions[i];
          const index = correctTerms.indexOf(term);
          if (index !== -1 && correctDescriptions[index] === userDesc) {
            correctCount++;
          }
        }

        const total = correctTerms.length;
        const isCorrect = total > 0 && correctCount === total;
        if (isCorrect) return acc;

        const questionsPayload = [
          leftCol,
          rightCol,
          {
            user_answer: userDescriptions,
          },
          {
            explanation: questionObj.explanation || "",
          },
        ];

        acc.push({ questions: questionsPayload });

        return acc;
      }

      const correctAnswer =
        questionObj.correct_answer || questionObj.answer || "";

      const userValue =
        rawUserAnswer &&
        typeof rawUserAnswer === "object" &&
        "value" in rawUserAnswer
          ? rawUserAnswer.value
          : rawUserAnswer;

      if (
        userValue === undefined ||
        userValue === null ||
        String(userValue).trim() === ""
      ) {
        return acc;
      }

      let isCorrect = false;

      if (
        type === "mcq" ||
        type === "quiz" ||
        type === "trueorfalse" ||
        type === "truefalse"
      ) {
        const normUser = String(userValue).trim().toLowerCase();
        const normCorrect = String(correctAnswer).trim().toLowerCase();
        isCorrect = normUser === normCorrect;
      } else if (type === "fillups") {
        const normUser = String(userValue || "")
          .trim()
          .toLowerCase();
        const normCorrect = String(correctAnswer || "")
          .trim()
          .toLowerCase();
        isCorrect = normUser === normCorrect;
      } else {
        return acc;
      }

      if (isCorrect) return acc;

      const options = buildOptions(questionObj);

      acc.push({
        question: questionObj.question || "",
        options,
        answer: correctAnswer,
        user_answer: userValue,
        explanation: questionObj.explanation || "",
      });

      return acc;
    }, []);
  };

  const handleTeachClick = async () => {
    setShowAiTutor(true);

    if (aiLessons || aiLoading) {
      return;
    }

    try {
      setAiLoading(true);
      setAiError(null);

      const user_data = buildUserData();
      const response = await sendAiTutorData({ user_data });

      let lessonsFromApi = [];

      if (response?.raw) {
        try {
          const parsedRaw =
            typeof response.raw === "string"
              ? JSON.parse(response.raw)
              : response.raw;
          if (Array.isArray(parsedRaw?.data)) {
            lessonsFromApi = parsedRaw.data;
          }
        } catch (e) {
          console.error("Failed to parse raw field:", e);
        }
      }

      if (
        (!lessonsFromApi || lessonsFromApi.length === 0) &&
        response?.structured
      ) {
        lessonsFromApi = response.structured.data ?? [];
      }

      if (
        (!lessonsFromApi || lessonsFromApi.length === 0) &&
        response?.content
      ) {
        try {
          const parsedContent = JSON.parse(response.content);
          if (Array.isArray(parsedContent?.data)) {
            lessonsFromApi = parsedContent.data;
          }
        } catch (e) {
          console.error("Failed to parse content field:", e);
        }
      }

      if (!Array.isArray(lessonsFromApi) || lessonsFromApi.length === 0) {
        setAiLessons([]);
        setAiError("No AI tutor lessons returned from server.");
      } else {
        const sorted = [...lessonsFromApi].sort(
          (a, b) => (a.sequence || 0) - (b.sequence || 0),
        );
        setAiLessons(sorted);
      }
    } catch (error) {
      console.error("Error in AI Tutor:", error);
      setAiError("Failed to load AI tutor content.");
      setAiLessons([]);
    } finally {
      setAiLoading(false);
    }
  };

  const handleRegenerateAiTutor = async () => {
    try {
      setAiLoading(true);
      setAiError(null);

      const user_data = buildUserData();
      const response = await sendAiTutorData(
        { user_data },
        {
          testPageId: pageId,
          title: pageTitle,
          key: pageKey,
          regenerate: true,
        }
      );

      let lessonsFromApi = [];

      if (response?.raw) {
        try {
          const parsedRaw =
            typeof response.raw === "string"
              ? JSON.parse(response.raw)
              : response.raw;
          if (Array.isArray(parsedRaw?.data)) {
            lessonsFromApi = parsedRaw.data;
          }
        } catch (e) {
          console.error("Failed to parse raw field:", e);
        }
      }

      if (
        (!lessonsFromApi || lessonsFromApi.length === 0) &&
        response?.structured
      ) {
        lessonsFromApi = response.structured.data ?? [];
      }

      if (
        (!lessonsFromApi || lessonsFromApi.length === 0) &&
        response?.content
      ) {
        try {
          const parsedContent = JSON.parse(response.content);
          if (Array.isArray(parsedContent?.data)) {
            lessonsFromApi = parsedContent.data;
          }
        } catch (e) {
          console.error("Failed to parse content field:", e);
        }
      }

      if (!Array.isArray(lessonsFromApi) || lessonsFromApi.length === 0) {
        setAiLessons([]);
        setAiError("No AI tutor lessons returned from server.");
      } else {
        const sorted = [...lessonsFromApi].sort(
          (a, b) => (a.sequence || 0) - (b.sequence || 0),
        );
        setAiLessons(sorted);
      }
    } catch (error) {
      console.error("Error in AI Tutor regeneration:", error);
      setAiError("Failed to load AI tutor content.");
      setAiLessons([]);
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="test-results-container">
      {showConfetti && !showAiTutor && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={200}
          gravity={0.1}
        />
      )}
      {showAiTutor && (
        <>
          <button
            type="button"
            className="ai-tutor-close-button"
            onClick={() => setShowAiTutor(false)}
            aria-label="Close AI Tutor"
          >
            ×
          </button>
          <AiTutor
            lessons={aiLessons}
            isLoading={aiLoading}
            error={aiError}
            pageId={pageId}
            pageKey={pageKey}
            pageTitle={pageTitle}
            onRegenerate={handleRegenerateAiTutor}
            isRegenerating={aiLoading}
          />
        </>
      )}
      {!showAiTutor && (
        <div className="score-container">
          <div className="score-chart-wrapper">
            <AnimatedCircularProgress score={percentage} />
          </div>

          <div
            className="score-actions"
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "10px",
              alignItems: "center",
              marginTop: "20px",
            }}
          >
            <button
              className="btn-primary"
              onClick={onRetest}
              style={{
                padding: "10px 30px",
                borderRadius: "8px",
                fontSize: "16px",
                minWidth: "150px",
                border: "none",
              }}
            >
              Reattempt
            </button>
            <button
              className="btn-primary"
              onClick={onShowAnswer}
              style={{
                padding: "10px 30px",
                borderRadius: "8px",
                fontSize: "16px",
                minWidth: "150px",
                border: "none",
              }}
            >
              Show Answer
            </button>
            {showTeachButton && (
              <button
                className="btn-primary"
                onClick={handleTeachClick}
                style={{
                  padding: "10px 30px",
                  borderRadius: "8px",
                  fontSize: "16px",
                  minWidth: "150px",
                }}
              >
                Teach
              </button>
            )}
          </div>
        </div>
      )}
      <AiGenerationPopup
        isOpen={showAiPopup}
        onClose={() => setShowAiPopup(false)}
        onGenerate={(difficulty) => {
          setShowAiPopup(false);
        }}
        questions={questions}
      />
    </div>
  );
};

export default TestResults;
